import React from 'react';
import { Box, Typography, FormControlLabel, Select, MenuItem, Paper, Divider, TextField, IconButton, Stack, Avatar, CircularProgress, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Button from '@mui/material/Button';
import { ENDPOINTS } from '../config/api';

interface SettingsPageProps {
  onClose: () => void;
}

// Add this at the top of the file, after imports
function getJwtFromCookie() {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Utility to fetch user info from backend
async function fetchUserData() {
  const jwt = getJwtFromCookie();
  if (!jwt) return null;
  try {
    const response = await fetch(ENDPOINTS.USERS_ME, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      return data.data || data;
    }
  } catch (error) {
    console.error('Failed to fetch user data:', error);
  }
  return null;
}

const OPENAI_TTS_VOICES = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'nova', label: 'Nova' },
  { value: 'shimmer', label: 'Shimmer' },
];

const TTS_SAMPLE_TEXT = 'This is a sample of the selected voice.';

const getStoredVoice = () => localStorage.getItem('ttsVoice') || 'alloy';
const setStoredVoice = (voice: string) => localStorage.setItem('ttsVoice', voice);

const SettingsPage: React.FC<SettingsPageProps> = ({ onClose }) => {
  const [ttsVoice, setTtsVoice] = React.useState<string>(getStoredVoice());
  const [playingVoice, setPlayingVoice] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<any>(null);
  const [loadingUser, setLoadingUser] = React.useState(true);

  React.useEffect(() => {
    const loadUser = async () => {
      setLoadingUser(true);
      const userData = await fetchUserData();
      setUser(userData);
      setLoadingUser(false);
    };
    loadUser();
  }, []);

  const handleVoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTtsVoice(event.target.value);
    setStoredVoice(event.target.value);
  };

  const playSample = async (voice: string) => {
    setPlayingVoice(voice);
    try {
      const jwt = getJwtFromCookie();
      const res = await fetch('/api/v1/ai/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ text: TTS_SAMPLE_TEXT, voice }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch sample audio');
      const audioBlob = await res.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => {
        setPlayingVoice(null);
        URL.revokeObjectURL(audioUrl);
      };
    } catch (err) {
      setPlayingVoice(null);
      alert('Could not play sample: ' + (err as Error).message);
    }
  };

  return (
    <Paper elevation={4} sx={{ maxWidth: 700, mx: 'auto', mt: 6, p: 4, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <IconButton onClick={onClose} aria-label="Back" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Typography variant="h5" sx={{ flexGrow: 1, textAlign: 'center' }}>
          Settings & Preferences
        </Typography>
        <Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Stack>
      <Divider sx={{ mb: 3 }} />
      {/* User Profile Card */}
      <Box sx={{ mb: 4, p: 2, borderRadius: 2, bgcolor: 'background.default', display: 'flex', alignItems: 'center', gap: 3, boxShadow: 1 }}>
        {loadingUser ? (
          <CircularProgress size={40} />
        ) : user ? (
          <>
            <Avatar src={user.picture} alt={user.name || user.email} sx={{ width: 64, height: 64, fontSize: 32 }}>
              {user.name ? user.name[0] : user.email[0]}
            </Avatar>
            <Box>
              <Typography variant="h6">{user.name || 'No Name'}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              {user.plan && (
                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>Plan: {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}</Typography>
              )}
              {user.customerId && (
                <Typography variant="body2" color="text.secondary">Customer ID: {user.customerId}</Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>User ID: {user.id}</Typography>
              <Typography variant="body2" color="text.secondary">Created: {user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">Updated: {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}</Typography>
            </Box>
          </>
        ) : (
          <Typography color="error">Could not load user profile.</Typography>
        )}
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Language</Typography>
        <Select value="en" onChange={() => {}} sx={{ minWidth: 120 }}>
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="es">Español</MenuItem>
          <MenuItem value="fr">Français</MenuItem>
        </Select>
      </Box>
      <Box sx={{ mb: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
            Toolbar Layout
          </FormLabel>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Choose how you want the toolbar to be displayed. Horizontal layout shows controls in a row, while vertical layout stacks them in a column.
          </Typography>
          <RadioGroup
            value={user?.settings?.toolbarLayout || 'horizontal'}
            onChange={async (event) => {
              const newLayout = event.target.value as 'horizontal' | 'vertical' | 'bottom';
              try {
                const jwt = getJwtFromCookie();
                const response = await fetch(ENDPOINTS.USERS_SETTINGS, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    settings: {
                      ...user?.settings,
                      toolbarLayout: newLayout
                    }
                  }),
                });
                if (response.ok) {
                  // Update local state
                  setUser((prev: any) => prev ? {
                    ...prev,
                    settings: {
                      ...prev.settings,
                      toolbarLayout: newLayout
                    }
                  } : null);
                }
              } catch (error) {
                console.error('Failed to update toolbar layout:', error);
              }
            }}
          >
            <FormControlLabel
              value="horizontal"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Horizontal</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.5, 
                    p: 0.5, 
                    border: '1px solid #ccc', 
                    borderRadius: 1,
                    width: 60,
                    height: 20
                  }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                  </Box>
                </Box>
              }
            />
            <FormControlLabel
              value="vertical"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Vertical</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5, 
                    p: 0.5, 
                    border: '1px solid #ccc', 
                    borderRadius: 1,
                    width: 20,
                    height: 60
                  }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                  </Box>
                </Box>
              }
            />
            <FormControlLabel
              value="bottom"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Bottom</Typography>
                  <Box sx={{
                    display: 'flex',
                    gap: 0.5,
                    p: 0.5,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    width: 60,
                    height: 20,
                    alignItems: 'flex-end',
                    background: 'linear-gradient(to top, #e3f2fd 60%, transparent 100%)',
                  }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5, mb: 0.5 }} />
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5, mb: 0.5 }} />
                  </Box>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ mb: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
            AI Voice (Read Aloud)
          </FormLabel>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Choose your preferred AI voice for read-aloud responses. You can preview each voice before selecting.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.03)',
              borderRadius: 2,
              p: 2,
            }}
          >
            {OPENAI_TTS_VOICES.map((voice) => (
              <Box
                key={voice.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  border: ttsVoice === voice.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  background: ttsVoice === voice.value ? 'rgba(25, 118, 210, 0.07)' : 'transparent',
                  transition: 'border 0.2s, background 0.2s',
                  minWidth: 0,
                  flexWrap: 'wrap',
                  mb: { xs: 0, sm: 0 },
                }}
              >
                <Radio
                  checked={ttsVoice === voice.value}
                  onChange={handleVoiceChange}
                  value={voice.value}
                  name="tts-voice"
                  color="primary"
                  sx={{ mr: 1 }}
                />
                <Typography sx={{ fontWeight: 500, minWidth: 60 }}>{voice.label}</Typography>
                <Button
                  size="small"
                  variant={ttsVoice === voice.value ? 'contained' : 'outlined'}
                  sx={{ ml: 2, minWidth: 100, textTransform: 'none', fontWeight: 500, boxShadow: 'none', flexShrink: 0 }}
                  disabled={playingVoice === voice.value}
                  onClick={() => playSample(voice.value)}
                >
                  {playingVoice === voice.value ? 'Playing...' : 'Play Sample'}
                </Button>
              </Box>
            ))}
          </Box>
        </FormControl>
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Keyboard Shortcuts</Typography>
        <TextField fullWidth label="Ask Shortcut" value="Cmd+Enter" sx={{ mb: 1 }} />
        <TextField fullWidth label="Show/Hide Shortcut" value="Cmd+\" sx={{ mb: 1 }} />
      </Box>
    </Paper>
  );
};

export default SettingsPage; 